import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Alert,
  Typography,
  IconButton,
  Popover,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useIntersection } from '@mantine/hooks';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import { FetchNextPageOptions, InfiniteQueryObserverResult } from '@tanstack/react-query';
import { TWidgetAlert } from 'utility/types/dashboard';
import { IntersectionLoader } from 'components/generic/IntersectionLoader';

type TWidgetAlertsListProps = {
  alerts: TWidgetAlert[];
  isLoading: boolean;
  isFetching?: boolean;
  onSelectAlert: (alert: TWidgetAlert) => void;
  onCreateNew: () => void;
  onDeleteAlert: (alertId: string) => void;
  deletingAlertId?: string | null;
  hasNextPage?: boolean;
  fetchNextPage?: (
    options?: FetchNextPageOptions,
  ) => Promise<InfiniteQueryObserverResult>;
  isFetchingNextPage?: boolean;
  intersectionRef?: ReturnType<typeof useIntersection>['ref'];
  intersectionEntry?: ReturnType<typeof useIntersection>['entry'];
};

const WidgetAlertsList = ({
  alerts,
  isLoading,
  onSelectAlert,
  onCreateNew,
  onDeleteAlert,
  deletingAlertId,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  intersectionRef,
  intersectionEntry,
}: TWidgetAlertsListProps) => {
  const { t } = useTranslation();
  const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLElement | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const handleDeleteClick = (event: React.MouseEvent<HTMLElement>, alertId: string) => {
    event.stopPropagation();
    setDeleteAnchorEl(event.currentTarget);
    setPendingDeleteId(alertId);
  };

  const handleDeleteCancel = () => {
    setDeleteAnchorEl(null);
    setPendingDeleteId(null);
  };

  const handleDeleteConfirm = () => {
    if (pendingDeleteId) {
      onDeleteAlert(pendingDeleteId);
    }
    setDeleteAnchorEl(null);
    setPendingDeleteId(null);
  };

  const isDeletePopoverOpen = Boolean(deleteAnchorEl);

  return (
    <Box
      data-testid="widget-alerts-list"
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 2, py: 1 }}
    >
      {/* Create New Alert Button */}
      <Button
        data-testid="widget-alerts-create-new-button"
        variant="contained"
        startIcon={<AddIcon />}
        onClick={onCreateNew}
        disabled={isLoading}
        disableElevation
        fullWidth
      >
        {t('blox.dashboards.alerts.create_new') || 'Create New Alert'}
      </Button>

      {/* Alerts List */}
      {isLoading ? (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ textAlign: 'center', py: 2 }}
        >
          {t('blox.loading') || 'Loading...'}
        </Typography>
      ) : alerts.length === 0 ? (
        <Alert
          data-testid="widget-alerts-no-alerts"
          severity="info"
          variant="outlined"
        >
          {t('blox.dashboards.alerts.errors.no_alerts') || 'No alerts set'}
        </Alert>
      ) : (
        <List
          data-testid="widget-alerts-items"
          sx={{ width: '100%' }}
        >
          {alerts.map((alert) => (
            <ListItem
              key={alert.id}
              data-testid={`widget-alert-item--${alert.id}`}
              onClick={() => onSelectAlert(alert)}
              sx={{
                cursor: 'pointer',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
              secondaryAction={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton
                    edge={false}
                    size="small"
                    data-testid={`widget-alert-delete-button--${alert.id}`}
                    aria-label={t('blox.actions.delete') || 'Delete'}
                    disabled={deletingAlertId === alert.id}
                    onClick={(e) => handleDeleteClick(e, alert.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  <IconButton edge="end">
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              }
            >
              <ListItemText
                primary={alert.title}
                secondary={
                  <Box component="span">
                    <Typography
                      component="span"
                      variant="body2"
                      color="textSecondary"
                    >
                      {t('blox.dashboards.alerts.cadence') || 'Cadence'}: {alert.cadence}
                    </Typography>
                    {alert.domains && alert.domains.length > 0 && (
                      <Typography
                        component="span"
                        variant="body2"
                        color="textSecondary"
                        sx={{ ml: 2 }}
                      >
                        {t('blox.dashboards.alerts.domains') || 'Domains'}:{' '}
                        {alert.domains.length}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
          {/* Infinite scroll loader */}
          {intersectionRef && intersectionEntry && fetchNextPage && (
            <IntersectionLoader
              hasNextPage={hasNextPage ?? false}
              isFetching={isFetchingNextPage ?? false}
              fetchNextPage={fetchNextPage}
              ixRef={intersectionRef}
              entry={intersectionEntry}
              endOfResultsMessage={t('blox.dashboards.alerts.no_more_alerts')}
              polymorph="li"
              compact
            />
          )}{' '}
        </List>
      )}

      {/* Delete confirmation popover */}
      <Popover
        data-testid="widget-alert-delete-popover"
        open={isDeletePopoverOpen}
        anchorEl={deleteAnchorEl}
        onClose={handleDeleteCancel}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box
          data-testid="widget-alert-delete-popover-content"
          sx={{ p: 2, maxWidth: 260 }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, mb: 0.5 }}
          >
            {t('blox.dashboards.alerts.delete.title')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 2,
            }}
          >
            {t('blox.dashboards.alerts.delete.warning')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              data-testid="widget-alert-delete-cancel-button--list"
              size="small"
              onClick={handleDeleteCancel}
              disableElevation
            >
              {t('blox.actions.cancel')}
            </Button>
            <Button
              data-testid="widget-alert-delete-confirm-button--list"
              size="small"
              variant="contained"
              color="error"
              onClick={handleDeleteConfirm}
              disableElevation
            >
              {t('blox.actions.delete')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default WidgetAlertsList;
